import { useState, useEffect, useCallback } from 'react';

// Detects iOS devices (iPhone/iPad/iPod). iOS Safari doesn't support
// beforeinstallprompt, so we show manual "Add to Home Screen" instructions instead.
function isIosDevice() {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ reports as "MacIntel" with touch support, so check that too.
  const isIpadOS13Plus =
    ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
  return isIos || isIpadOS13Plus;
}

// Detects whether the app is currently running as an installed PWA
// (Step 10 requirement) using both the standard media query and the
// iOS-specific navigator.standalone flag.
function isRunningStandalone() {
  const matchesDisplayMode = window.matchMedia(
    '(display-mode: standalone)'
  ).matches;
  const isIosStandalone = window.navigator.standalone === true;
  return matchesDisplayMode || isIosStandalone;
}

/**
 * usePWAInstall
 *
 * Reusable hook exposing PWA install state and actions so any component
 * (header, profile menu, dashboard, settings) can trigger the same
 * install flow without duplicating logic.
 *
 * Returns:
 *  - canInstall: true when the native beforeinstallprompt is ready to fire
 *  - isInstalled: true when the app is already running standalone
 *  - isIos: true on iPhone/iPad (needs manual instructions, no native prompt)
 *  - installApp(): triggers the native install dialog (Android/Desktop Chrome/Edge)
 *  - installStatus: 'idle' | 'accepted' | 'dismissed' | 'unavailable'
 */
export default function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isRunningStandalone());
  const [installStatus, setInstallStatus] = useState('idle');
  const [isIos] = useState(isIosDevice());

  useEffect(() => {
    // Capture the native install prompt event (Step 6.2) instead of letting
    // the browser show its own mini-infobar automatically.
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    // Step 6.6: detect successful installation (fires after the user
    // accepts, regardless of which button/flow triggered it).
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setInstallStatus('accepted');
    };

    // Keep isInstalled in sync if the display-mode changes at runtime
    // (e.g. user installs from browser menu instead of our button).
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (event) => {
      if (event.matches) setIsInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    standaloneQuery.addEventListener
      ? standaloneQuery.addEventListener('change', handleDisplayModeChange)
      : standaloneQuery.addListener(handleDisplayModeChange); // Safari fallback

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneQuery.removeEventListener
        ? standaloneQuery.removeEventListener('change', handleDisplayModeChange)
        : standaloneQuery.removeListener(handleDisplayModeChange);
    };
  }, []);

  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      setInstallStatus('unavailable');
      return { outcome: 'unavailable' };
    }

    // Step 6.5: only trigger the native dialog after the user clicks.
    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      setInstallStatus('accepted');
    } else {
      setInstallStatus('dismissed');
    }

    // The prompt can only be used once; clear it either way.
    setDeferredPrompt(null);
    return choiceResult;
  }, [deferredPrompt]);

  return {
    canInstall: Boolean(deferredPrompt) && !isInstalled,
    isInstalled,
    isIos,
    installApp,
    installStatus,
  };
}